import type { SpeakerSegment } from '../types.js';

interface TranscriptViewProps {
  segments: SpeakerSegment[];
  rawText?: string | null;
}

function parseSpeakerSegments(raw: unknown): SpeakerSegment[] | null {
  if (!raw) return null;
  try {
    const arr = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (!Array.isArray(arr)) return null;
    return arr.map((item: { speaker?: string; text?: string; start?: number; end?: number }, i) => ({
      speaker: item.speaker ?? (i % 2 === 0 ? 'A' : 'B'),
      text: item.text ?? '',
      start: item.start,
      end: item.end,
    }));
  } catch {
    return null;
  }
}

function formatTime(seconds: number | undefined): string {
  if (seconds === undefined) return '';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

// Normalize speaker label
function speakerLabel(speaker: string): { id: 'A' | 'B'; label: string } {
  const s = speaker.toLowerCase();
  if (s === 'agent' || s === 'a' || s === '0' || s === 'speaker_0') return { id: 'A', label: 'Agent' };
  return { id: 'B', label: 'Caller' };
}

export function TranscriptView({ segments, rawText }: TranscriptViewProps) {
  if (segments.length === 0 && rawText) {
    // Render as plain text if no structured segments
    return (
      <div className="max-h-80 overflow-y-auto pr-1">
        <pre className="text-xs text-slate-400 whitespace-pre-wrap leading-relaxed font-mono">{rawText}</pre>
      </div>
    );
  }

  if (segments.length === 0) {
    return (
      <p className="text-slate-600 text-sm italic">No transcript available.</p>
    );
  }

  return (
    <div className="space-y-3 max-h-80 overflow-y-auto pr-1">
      {segments.map((seg, i) => {
        const { id, label } = speakerLabel(seg.speaker);
        return (
          <div key={i} className={`flex gap-3 ${id === 'B' ? 'flex-row-reverse' : ''}`}>
            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5
              ${id === 'A' ? 'bg-blue-600/30 text-blue-400' : 'bg-emerald-600/30 text-emerald-400'}`}>
              {id}
            </div>
            <div className="flex-1 max-w-[85%]">
              <div className={`flex items-center gap-2 mb-1 ${id === 'B' ? 'flex-row-reverse' : ''}`}>
                <span className={`text-xs font-medium ${id === 'A' ? 'text-blue-400' : 'text-emerald-400'}`}>
                  {label}
                </span>
                {seg.start !== undefined && (
                  <span className="text-xs text-slate-600">{formatTime(seg.start)}</span>
                )}
              </div>
              <div className={`rounded-xl px-3 py-2 text-sm text-slate-300 leading-relaxed
                ${id === 'A'
                  ? 'bg-blue-600/10 border border-blue-600/20 rounded-tl-none'
                  : 'bg-emerald-600/10 border border-emerald-600/20 rounded-tr-none'}`}>
                {seg.text}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Helper to parse transcript data from the API
export { parseSpeakerSegments };
